import {IDefaultGameEventData, IGameEventData} from './Types';
import {DefaultGameEventTypes} from './Constants';

export class GameEvents {
    /**
     * Is sent after Spin request was send to game server.
     * @param {number} messageId - id of request message
     * @param {number} unpaidWin - win payment not confirmed by Game Server
     * @param {number} gameRoundId - game round id
     * @param {Object} customData - custom data
     * @returns {IDefaultGameEventData}
     * @constructor
     */
    public static PrimaryGameEscrow(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.PrimaryGameEscrow, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Game client has received spin response, but animation still runs on game client.
     * @param {number} messageId - id of request message
     * @param {number} unpaidWin - win payment not confirmed by Game Server
     * @param {number} gameRoundId - game round id
     * @param {Object} customData - custom data
     * @returns {IDefaultGameEventData}
     * @constructor
     */
    public static PrimaryGameStarted(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.PrimaryGameStarted, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Must be sent immediately after game client stops reels animation and (or) after main win count animation.
     * @param {number} messageId - id of request message
     * @param {number} unpaidWin - win payment not confirmed by Game Server
     * @param {number} gameRoundId - game round id
     * @param {Object} customData - custom data
     * @returns {IDefaultGameEventData}
     * @constructor
     */
    public static PrimaryGameEnded(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.PrimaryGameEnded, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Must be send after the player enters into gamble feature. Start gamble request was sent.
     * @param {number} messageId - id of request message
     * @param {number} unpaidWin - win payment not confirmed by Game Server
     * @param {number} gameRoundId - game round id
     * @param {Object} customData - custom data
     * @returns {IDefaultGameEventData}
     * @constructor
     */
    public static SecondaryGameEntered(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.SecondaryGameEntered, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * The Secondary Game Halved state applies when player chooses Halve action which halves his wager available for Secondary Game.
     * The transition from Secondary Game Halved will lead to Secondary Game Choice state.
     * @param {number} messageId - id of request message
     * @param {number} unpaidWin - win payment not confirmed by Game Server
     * @param {number} gameRoundId - game round id
     * @param {Object} customData - custom data
     * @returns {IDefaultGameEventData}
     * @constructor
     */
    public static SecondaryGameHalved(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.SecondaryGameHalved, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Must be sent after the client has sent gamble select request to game server.
     * @param {number} messageId - id of request message
     * @param {number} unpaidWin - win payment not confirmed by Game Server
     * @param {number} gameRoundId - game round id
     * @param {Object} customData - custom data
     * @returns {IDefaultGameEventData}
     * @constructor
     */
    public static SecondaryGameEscrow(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.SecondaryGameEscrow, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Must be sent after the client has received Gamble start response from game server.
     * @param {number} messageId - id of request message
     * @param {number} unpaidWin - win payment not confirmed by Game Server
     * @param {number} gameRoundId - game round id
     * @param {Object} customData - custom data
     * @returns {IDefaultGameEventData}
     * @constructor
     */
    public static SecondaryGameStarted(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.SecondaryGameStarted, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Must be sent after the Collect request was sent to game server.
     * @param {number} messageId - id of request message
     * @param {number} unpaidWin - win payment not confirmed by Game Server
     * @param {number} gameRoundId - game round id
     * @param {Object} customData - custom data
     * @returns {IDefaultGameEventData}
     * @constructor
     */
    public static GameCollectEscrow(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.GameCollectEscrow, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Collect request was sent to game server from gamble feature.
     * @param {number} messageId - id of request message
     * @param {number} unpaidWin - win payment not confirmed by Game Server
     * @param {number} gameRoundId - game round id
     * @param {Object} customData - custom data
     * @returns {IDefaultGameEventData}
     * @constructor
     */
    public static SecondaryGameCollectEscrow(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.SecondaryGameCollectEscrow, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Must be sent always when the gamble feature ends.
     * @param {number} messageId - id of request message
     * @param {number} unpaidWin - win payment not confirmed by Game Server
     * @param {number} gameRoundId - game round id
     * @param {Object} customData - custom data
     * @returns {IDefaultGameEventData}
     * @constructor
     */
    public static SecondaryGameEnded(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.SecondaryGameEnded, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Collect response was received from game server.
     * @param {number} messageId - id of request message
     * @param {number} unpaidWin - win payment not confirmed by Game Server
     * @param {number} gameRoundId - game round id
     * @param {Object} customData - custom data
     * @returns {IDefaultGameEventData}
     * @constructor
     */
    public static GameEnded(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.GameEnded, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Autoplay started.
     * @param {number} messageId
     * @param {number} unpaidWin
     * @param {number} gameRoundId
     * @param {Object} customData
     * @returns {IGameEventData}
     * @constructor
     */
    public static AutoPlayStarted(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.AutoPlayStarted, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Autoplay ended.
     * @param {number} messageId
     * @param {number} unpaidWin
     * @param {number} gameRoundId
     * @param {Object} customData
     * @returns {IGameEventData}
     * @constructor
     */
    public static AutoPlayEnded(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.AutoPlayEnded, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Game play was enabled.
     * @param {number} messageId
     * @param {number} unpaidWin
     * @param {number} gameRoundId
     * @param {Object} customData
     * @returns {IGameEventData}
     * @constructor
     */
    public static GamePlayEnabled(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.GamePlayEnabled, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Game play was disabled.
     * @param {number} messageId
     * @param {number} unpaidWin
     * @param {number} gameRoundId
     * @param {Object} customData
     * @returns {IGameEventData}
     * @constructor
     */
    public static GamePlayDisabled(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.GamePlayDisabled, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Bonus game started (e.g. freespins, respins, pick, ...) must be sent immediately after was received spin/respin/picks
     * response from game server - for each spin of free spin or in case of pick or respin.
     * @param {number} messageId
     * @param {number} unpaidWin
     * @param {number} gameRoundId
     * @param {Object} customData
     * @returns {IGameEventData}
     * @constructor
     */
    public static BonusGameStarted(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.BonusGameStarted, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Reels animation or win animation which starts freespin, pick or respin feature is ended.
     * @param {number} messageId
     * @param {number} unpaidWin
     * @param {number} gameRoundId
     * @param {Object} customData
     * @returns {IGameEventData}
     * @constructor
     */
    public static BonusGameTriggered(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.BonusGameTriggered, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * After Spin/Respin/Pick request was sent to game server.
     * @param {number} messageId
     * @param {number} unpaidWin
     * @param {number} gameRoundId
     * @param {Object} customData
     * @returns {IGameEventData}
     * @constructor
     */
    public static BonusGameEscrow(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.BonusGameEscrow, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * BonusGameEnded (e.g. free spins, respins, pick bonus) must be sent after all reel animations ends
     * (reels are stopped) and/or all win animations ends and win box is updated (when there is a win).
     * @param {number} messageId
     * @param {number} unpaidWin
     * @param {number} gameRoundId
     * @param {Object} customData
     * @returns {IGameEventData}
     * @constructor
     */
    public static BonusGameEnded(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.BonusGameEnded, messageId, unpaidWin, gameRoundId, customData);
    }

    /**
     * Game was successfully loaded.
     * @param {number} messageId
     * @param {number} unpaidWin
     * @param {number} gameRoundId
     * @param {Object} customData
     * @returns {IGameEventData}
     * @constructor
     */
    public static GameLoaded(messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IGameEventData {
        return createGameEvent(DefaultGameEventTypes.GameLoaded, messageId, unpaidWin, gameRoundId, customData);
    }
}

function createGameEvent(event: string, messageId: number, unpaidWin: number, gameRoundId: number, customData?: Object): IDefaultGameEventData {
    return {
        event: event,
        messageId: messageId,
        unpaidWin: unpaidWin,
        gameRoundId: gameRoundId,
        customData: customData
    };
}


/** WEBPACK FOOTER **
 ** ./Engine/ClientHost/GameEvents.ts
 **/